import React from "react";
import { Link } from "react-router-dom";
import { Printer, ArrowLeft, PawPrint, MessageCircle, MapPin } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { QRCard } from "@/components/QRCard";
import { WHATSAPP_DISPLAY } from "@/lib/pricing";

const Benefit = ({ title, text }) => (
  <div className="flex gap-3">
    <div className="mt-1 shrink-0 w-6 h-6 rounded-full bg-[#4A8F5F] grid place-items-center text-[#F8F0DC] font-bold">✓</div>
    <div>
      <div className="font-bold text-[#F8F0DC] text-[14pt] leading-tight" style={{ fontFamily: "Fraunces, serif" }}>{title}:</div>
      <div className="text-[#F8F0DC] text-[12pt] leading-snug">{text}</div>
    </div>
  </div>
);

export default function PanfletoPromocional() {
  return (
    <div className="min-h-screen bg-neutral-200 py-8 px-4" data-testid="panfleto-promocional">
      <div className="no-print max-w-3xl mx-auto flex items-center justify-between mb-6">
        <Link to="/panfletos" className="inline-flex items-center gap-1 text-[#2C6E49] font-bold text-sm">
          <ArrowLeft size={16} /> Voltar
        </Link>
        <button onClick={() => window.print()} data-testid="print-promo" className="inline-flex items-center gap-2 rounded-full bg-[#2C6E49] text-[#F8F0DC] px-5 py-2.5 font-bold text-sm hover:bg-[#1F5F3F]">
          <Printer size={16} /> Imprimir / Salvar PDF
        </button>
      </div>

      <div className="print-page grain-overlay">
        {/* paw prints decorative */}
        <div className="paw-bg absolute inset-y-0 right-0 w-20 opacity-40" />
        <div className="paw-bg absolute inset-y-0 left-0 w-14 opacity-30" />

        {/* Header row: title + logo */}
        <div className="flex items-start justify-between gap-6 relative z-10">
          <div>
            <h1 className="text-[#2C6E49] font-bold leading-[0.95]" style={{ fontFamily: "Fraunces, serif", fontSize: "42pt" }}>
              SEU CÃOZINHO<br />
              PASSA MUITO<br />
              TEMPO<br />
              <span className="inline-flex items-center gap-3">
                SOZINHO? <PawPrint size={40} className="text-[#8B5A2B]" />
              </span>
            </h1>
          </div>
          <div className="flex flex-col items-center gap-1 shrink-0">
            <LogoMark height={110} />
          </div>
        </div>

        {/* Sub headline */}
        <p className="mt-4 text-[#1a1a1a] text-[13pt] leading-snug max-w-[52%] relative z-10" style={{ fontFamily: "Fraunces, serif", fontWeight: 500 }}>
          Eu posso ajudar! Proporcione mais saúde, gasto de energia e felicidade para o seu melhor amigo.
        </p>

        {/* Middle: illustration placeholder + benefits panel */}
        <div className="mt-6 grid grid-cols-12 gap-4 relative z-10">
          <div className="col-span-7">
            <div className="rounded-3xl overflow-hidden border-4 border-[#4A8F5F]/40">
              <img
                alt="passeios felizes na praia"
                src="https://images.unsplash.com/photo-1548199973-03cce0bbc87b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                className="w-full h-[240px] object-cover"
              />
            </div>
          </div>
          <div className="col-span-5">
            <div className="rounded-3xl bg-[#2C6E49] px-5 py-5 shadow-lg">
              <div className="rounded-full bg-[#4A8F5F] text-[#F8F0DC] text-center py-2 mb-4" style={{ fontFamily: "Fraunces, serif" }}>
                <span className="text-[13pt] font-bold">POR QUE CONTRATAR<br />A CÃOMIAR?</span>
              </div>
              <div className="flex flex-col gap-3">
                <Benefit title="Saúde e Bem-Estar" text="Reduz o estresse, a ansiedade e o tédio do seu pet." />
                <Benefit title="Passeios Seguros" text="Roteiros adaptados à energia e ao porte do seu cão." />
                <Benefit title="Tranquilidade pra Você" text="Envio de fotos e vídeos durante o passeio." />
              </div>
            </div>
          </div>
        </div>

        {/* Plans box */}
        <div className="mt-6 rounded-3xl bg-[#2C6E49] px-6 py-5 relative z-10">
          <div className="rounded-full bg-[#4A8F5F] text-[#F8F0DC] text-center py-1.5" style={{ fontFamily: "Fraunces, serif" }}>
            <span className="text-[13pt] font-bold">Nossos Planos:</span>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-3 text-[#F8F0DC]">
            <div className="flex items-center gap-2"><span className="w-6 h-6 rounded-full bg-[#4A8F5F] grid place-items-center text-[#F8F0DC] font-bold">✓</span><span className="font-bold text-[12pt]">Passeios Avulsos</span></div>
            <div className="flex items-center gap-2"><span className="w-6 h-6 rounded-full bg-[#4A8F5F] grid place-items-center text-[#F8F0DC] font-bold">✓</span><span className="font-bold text-[12pt]">Pacotes Semanais</span></div>
            <div className="flex items-center gap-2"><span className="w-6 h-6 rounded-full bg-[#4A8F5F] grid place-items-center text-[#F8F0DC] font-bold">✓</span><span className="font-bold text-[12pt]">Planos Mensais</span></div>
          </div>
        </div>

        {/* Bottom row */}
        <div className="mt-8 grid grid-cols-12 gap-4 relative z-10 items-center">
          <div className="col-span-3">
            <QRCard compact />
          </div>
          <div className="col-span-5">
            <div className="flex items-start gap-2 text-[#2C6E49]">
              <MapPin size={22} className="mt-0.5" />
              <div style={{ fontFamily: "Fraunces, serif" }} className="text-[13pt] font-bold leading-tight">
                Atendimento exclusivo no bairro Vila Mathias – Santos
              </div>
            </div>
          </div>
          <div className="col-span-4 text-right">
            <div style={{ fontFamily: "Fraunces, serif" }} className="text-[#2C6E49] font-bold text-[22pt] leading-none">Vamos passear?</div>
            <div className="text-[#1a1a1a] text-[12pt]">Entre em contato e agende<br />um horário para o seu pet!</div>
            <div className="mt-2 inline-flex items-center gap-2 text-[#2C6E49] font-bold text-[15pt]" style={{ fontFamily: "Fraunces, serif" }}>
              <MessageCircle size={20} /> WhatsApp: {WHATSAPP_DISPLAY}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
